import { Fragment, useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { CalendarMonthRow } from '@/hooks/useCalendarMonthMetrics'
import {
  financeGroupTotal,
  type FinanceExpenseGroup,
  type FinanceMonthlyFactRow,
} from '@/lib/financeMonthlyFacts'

type CurrencyMode = 'local' | 'rub'

type RevenueMetricKey =
  | 'regular_recurring'
  | 'private'
  | 'trial'
  | 'regular_new'
  | 'refunds'
  | 'subscription_recurring'
  | 'subscription_new'
  | 'other_income'
  | 'detail_new_sold'
  | 'detail_renewals_sold'
  | 'detail_upgrades_sold'
  | 'detail_returning_sold'
  | 'detail_start_clients'
  | 'detail_start_subscribers'
  | 'detail_active_clients'
  | 'detail_marketing_spend'
  | 'detail_leads'
  | 'detail_appointments'
  | 'detail_trial_visits'
  | 'detail_post_trial_purchases'
  | 'crm_regular_revenue'
  | 'crm_subscription_revenue'
  | 'total'

interface RevenueMetricConfig {
  key: RevenueMetricKey
  label: string
  format?: 'currency' | 'count'
}

interface RevenueSection {
  title: string
  rows: RevenueMetricConfig[]
}

const MONTH_LABELS = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']
const TABLE_REST_COL_SPAN = MONTH_LABELS.length + 1

const stickyMetricColumnClass = 'sticky left-0 z-10 w-[160px] min-w-[160px] max-w-[160px] bg-card py-2 pl-4 pr-3 text-left shadow-[1px_0_0_hsl(var(--border))] sm:w-[220px] sm:min-w-[220px] sm:max-w-[220px] lg:w-[280px] lg:min-w-[280px] lg:max-w-[280px]'
const stickySectionColumnClass = 'sticky left-0 z-10 w-[160px] min-w-[160px] max-w-[160px] bg-muted py-2 pl-4 pr-3 font-semibold text-left shadow-[1px_0_0_hsl(var(--border))] sm:w-[220px] sm:min-w-[220px] sm:max-w-[220px] lg:w-[280px] lg:min-w-[280px] lg:max-w-[280px]'
const stickyHeaderMetricColumnClass = 'sticky left-0 top-0 z-30 w-[160px] min-w-[160px] max-w-[160px] bg-card py-2 pl-4 pr-3 text-left shadow-[1px_0_0_hsl(var(--border)),0_1px_0_hsl(var(--border))] sm:w-[220px] sm:min-w-[220px] sm:max-w-[220px] lg:w-[280px] lg:min-w-[280px] lg:max-w-[280px]'
const monthValueColumnClass = 'w-[96px] min-w-[96px] sm:w-[104px] sm:min-w-[104px] lg:w-[112px] lg:min-w-[112px]'
const totalValueColumnClass = 'w-[120px] min-w-[120px] sm:w-[128px] sm:min-w-[128px] lg:w-[140px] lg:min-w-[140px]'
const stickyHeaderValueColumnClass = 'sticky top-0 z-20 bg-card py-2 px-3 text-right shadow-[0_1px_0_hsl(var(--border))]'

const REVENUE_SECTIONS: RevenueSection[] = [
  {
    title: 'Продажа абонементов',
    rows: [
      { key: 'regular_recurring', label: 'Абонементы постоянным клиентам', format: 'currency' },
      { key: 'private', label: 'Индивидуальные и сплит тренировки', format: 'currency' },
      { key: 'trial', label: 'Оплаты пробных занятий', format: 'currency' },
      { key: 'regular_new', label: 'Абонементы новым клиентам', format: 'currency' },
      { key: 'refunds', label: 'Возвраты клиентам', format: 'currency' }
    ]
  },
  {
    title: 'Продажа подписок',
    rows: [
      { key: 'subscription_recurring', label: 'Подписки постоянным клиентам', format: 'currency' },
      { key: 'subscription_new', label: 'Подписки новым клиентам', format: 'currency' }
    ]
  },
  {
    title: 'Другие доходы',
    rows: [{ key: 'other_income', label: 'Прочие продажи', format: 'currency' }]
  },
  {
    title: '',
    rows: [{ key: 'total', label: 'Итого за месяц', format: 'currency' }]
  },
  {
    title: 'Данные из Listok',
    rows: [
      { key: 'crm_regular_revenue', label: 'Выручка из Listok по абонементам', format: 'currency' },
      { key: 'crm_subscription_revenue', label: 'Выручка из Listok по подпискам', format: 'currency' },
      { key: 'detail_start_clients', label: 'Число клиентов на начало месяца', format: 'count' },
      { key: 'detail_start_subscribers', label: 'Число подписок на начало месяца', format: 'count' }
    ]
  },
  {
    title: 'Детализация по абонементам',
    rows: [
      { key: 'detail_new_sold', label: 'Продано абонементов новым клиентам', format: 'count' },
      { key: 'detail_renewals_sold', label: 'Продано абонементов продления', format: 'count' },
      { key: 'detail_upgrades_sold', label: 'Продано абонементов апгрейды', format: 'count' },
      { key: 'detail_returning_sold', label: 'Продано абонементов БЧС', format: 'count' },
      { key: 'detail_active_clients', label: 'Число активных клиентов', format: 'count' },
      { key: 'detail_marketing_spend', label: 'Потрачено на рекламу', format: 'currency' },
      { key: 'detail_leads', label: 'Заявок получено', format: 'count' },
      { key: 'detail_appointments', label: 'Записей на пробное', format: 'count' },
      { key: 'detail_trial_visits', label: 'Приходов на пробное', format: 'count' },
      { key: 'detail_post_trial_purchases', label: 'Покупок после пробного', format: 'count' }
    ]
  }
]

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

function formatExpenseMoney(value: number, currency: string): string {
  return formatMoney(value === 0 ? 0 : -Math.abs(value), currency)
}

function getMetricValue(row: CalendarMonthRow, metric: RevenueMetricKey, mode: CurrencyMode): number {
  const rub = mode === 'rub'
  if (metric === 'regular_new') return rub ? row.regular_new_amount_rub : row.regular_new_amount
  if (metric === 'regular_recurring') return rub ? row.regular_recurring_amount_rub : row.regular_recurring_amount
  if (metric === 'subscription_new') return rub ? row.subscription_new_amount_rub : row.subscription_new_amount
  if (metric === 'subscription_recurring') return rub ? row.subscription_recurring_amount_rub : row.subscription_recurring_amount
  if (metric === 'trial') return rub ? row.trial_payments_amount_rub : row.trial_payments_amount
  if (metric === 'private') return rub ? row.private_sessions_amount_rub : row.private_sessions_amount
  if (metric === 'other_income') return rub ? row.other_income_amount_rub : row.other_income_amount
  if (metric === 'refunds') return rub ? row.refunds_amount_rub : row.refunds_amount
  if (metric === 'detail_new_sold') return row.mixed_new_sales_count
  if (metric === 'detail_renewals_sold') return row.mixed_renewals_count
  if (metric === 'detail_upgrades_sold') return row.mixed_upgrades_count
  if (metric === 'detail_returning_sold') return row.mixed_returning_count
  if (metric === 'detail_start_clients') return row.start_clients_count
  if (metric === 'detail_start_subscribers') return row.start_subscribers_count
  if (metric === 'detail_active_clients') return row.active_clients_count
  if (metric === 'detail_marketing_spend') return rub ? row.marketing_spend_amount_rub : row.marketing_spend_amount
  if (metric === 'detail_leads') return row.leads_count
  if (metric === 'detail_appointments') return row.appointments_count
  if (metric === 'detail_trial_visits') return row.trial_visits_count
  if (metric === 'detail_post_trial_purchases') return row.purchases_after_trial_count
  if (metric === 'crm_regular_revenue') return rub ? row.crm_regular_revenue_rub : row.crm_regular_revenue
  if (metric === 'crm_subscription_revenue') return rub ? row.crm_subscription_revenue_rub : row.crm_subscription_revenue
  return rub ? row.gross_revenue_rub : row.gross_revenue
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 0
  }).format(value)
}

interface MonthlyRevenueTableProps {
  rows: CalendarMonthRow[]
  currencyMode: CurrencyMode
  selectedCurrency: string
  expenseGroups: FinanceExpenseGroup[]
  expenseRows: FinanceMonthlyFactRow[]
  expenseCurrency: string
  expenseLoading: boolean
  expenseError: string | null
  showExpenses: boolean
  reserveExpenseEditorSpace?: boolean
}

export function MonthlyRevenueTable({
  rows,
  currencyMode,
  selectedCurrency,
  expenseGroups,
  expenseRows,
  expenseCurrency,
  expenseLoading,
  expenseError,
  showExpenses,
  reserveExpenseEditorSpace = false,
}: MonthlyRevenueTableProps) {
  const [expandedExpenseGroups, setExpandedExpenseGroups] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    setExpandedExpenseGroups(new Set(expenseGroups.map(group => group.code)))
  }, [expenseGroups])
  const monthsWithData = useMemo(() => {
    const map = new Map<number, CalendarMonthRow>()
    for (const row of rows) {
      map.set(row.month, row)
    }
    return map
  }, [rows])

  const expensesByMonth = useMemo(() => (
    new Map(expenseRows.map(row => [Number(row.month_start.slice(5, 7)), row]))
  ), [expenseRows])

  const toggleExpenseGroup = (groupCode: string) => {
    setExpandedExpenseGroups(current => {
      const next = new Set(current)
      if (next.has(groupCode)) next.delete(groupCode)
      else next.add(groupCode)
      return next
    })
  }

  const visibleSections = useMemo(() => (
    REVENUE_SECTIONS
      .map((section) => ({
        ...section,
        rows: section.rows.filter((metric) =>
          MONTH_LABELS.some((_, monthIndex) => {
            const row = monthsWithData.get(monthIndex + 1)
            if (!row) return false
            return getMetricValue(row, metric.key, currencyMode) !== 0
          })
        )
      }))
      .filter((section) => section.rows.length > 0)
  ), [currencyMode, monthsWithData])

  const visibleExpenseGroups = useMemo(() => (
    expenseGroups
      .map(group => ({
        ...group,
        fields: group.fields.filter(field => MONTH_LABELS.some((_, monthIndex) => {
          const value = expensesByMonth.get(monthIndex + 1)?.values[field.code]
          return value !== undefined && value !== 0
        })),
      }))
      .filter(group => group.fields.length > 0)
  ), [expenseGroups, expensesByMonth])

  return (
    <div className="scrollbar-hide max-h-[65vh] overflow-auto bg-card text-sm">
      <table className={`relative isolate w-full min-w-[1432px] table-fixed border-separate border-spacing-0 text-sm sm:min-w-[1596px] lg:min-w-[1764px] ${reserveExpenseEditorSpace ? 'sm:mr-[592px]' : ''}`}>
        <colgroup>
          <col className="w-[160px] min-w-[160px] sm:w-[220px] sm:min-w-[220px] lg:w-[280px] lg:min-w-[280px]" />
          {MONTH_LABELS.map((monthLabel) => (
            <col key={monthLabel} className={monthValueColumnClass} />
          ))}
          <col className={totalValueColumnClass} />
        </colgroup>
        <thead>
          <tr className="font-semibold">
            <th className={stickyHeaderMetricColumnClass}>Статья</th>
            {MONTH_LABELS.map((monthLabel) => (
              <th key={monthLabel} className={stickyHeaderValueColumnClass}>
                {monthLabel}
              </th>
            ))}
            <th className="sticky top-0 z-20 bg-card py-2 pl-4 pr-5 text-right shadow-[0_1px_0_hsl(var(--border))]">
              Итого
            </th>
          </tr>
        </thead>
        <tbody>
        {showExpenses && (
          <tr className="bg-muted/60 font-semibold">
            <td className={stickySectionColumnClass}>Доходы · {selectedCurrency}</td>
            <td colSpan={TABLE_REST_COL_SPAN} className="bg-muted/60 py-2" />
          </tr>
        )}
        {visibleSections.map((section) => (
          <Fragment key={section.title || 'total-section'}>
            {section.title === 'Данные из Listok' && (
              <tr aria-hidden="true">
                <td className={`${stickyMetricColumnClass} bg-card`} />
                <td colSpan={TABLE_REST_COL_SPAN} className="py-1" />
              </tr>
            )}
            {section.title === 'Детализация по абонементам' && (
              <>
                <tr aria-hidden="true">
                  <td className={`${stickyMetricColumnClass} bg-card`} />
                  <td colSpan={TABLE_REST_COL_SPAN} className="py-2" />
                </tr>
                <tr aria-hidden="true">
                  <td className={`${stickyMetricColumnClass} bg-card`} />
                  <td colSpan={TABLE_REST_COL_SPAN} className="py-2" />
                </tr>
                <tr aria-hidden="true">
                  <td className={`${stickyMetricColumnClass} bg-card`} />
                  <td colSpan={TABLE_REST_COL_SPAN} className="py-2" />
                </tr>
              </>
            )}
            {section.title && (
              <tr className="border-t border-border bg-muted/30">
                <td className={stickySectionColumnClass}>
                  {section.title}
                </td>
                <td colSpan={TABLE_REST_COL_SPAN} className="bg-muted/30 py-2" />
              </tr>
            )}
            {section.rows.map((metric) => {
              const metricFormat = metric.format || 'currency'
              const values = MONTH_LABELS.map((_, monthIndex) => {
                const row = monthsWithData.get(monthIndex + 1)
                if (!row) return null
                return getMetricValue(row, metric.key, currencyMode)
              })
              const total = metric.key === 'detail_active_clients' || metric.key === 'detail_start_clients' || metric.key === 'detail_start_subscribers'
                ? (() => {
                    for (let monthIndex = MONTH_LABELS.length - 1; monthIndex >= 0; monthIndex--) {
                      const value = values[monthIndex]
                      if (value !== null) return value
                    }
                    return 0
                  })()
                : values.reduce((sum, value) => sum + (value ?? 0), 0)
              const isTotalRow = metric.key === 'total'

              return (
                <tr
                  key={metric.key}
                  className={isTotalRow ? 'border-t-2 border-border/80 font-semibold' : 'border-b border-border/50'}
                >
                  <td className={`${stickyMetricColumnClass} ${isTotalRow ? 'bg-muted py-3 text-foreground shadow-[1px_0_0_hsl(var(--border)),0_-1px_0_hsl(var(--border))]' : ''}`}>
                    {metric.label}
                  </td>
                  {values.map((value, idx) => (
                    <td
                      key={`${metric.key}-${idx}`}
                      className={`whitespace-nowrap px-3 text-right ${isTotalRow ? 'bg-muted/40 py-3 text-foreground font-semibold border-y border-border/70' : 'py-2'}`}
                    >
                      {value === null
                        ? '—'
                        : metricFormat === 'count'
                        ? formatCount(value)
                        : formatMoney(value, selectedCurrency)}
                    </td>
                  ))}
                  <td
                    className={`whitespace-nowrap pl-4 pr-5 text-right ${isTotalRow ? 'bg-muted/40 py-3 text-foreground font-bold border-y border-border/70' : 'py-2'}`}
                  >
                    {metricFormat === 'count' ? formatCount(total) : formatMoney(total, selectedCurrency)}
                  </td>
                </tr>
              )
            })}
          </Fragment>
        ))}
        {showExpenses && <>
        <tr aria-hidden="true">
          <td className={`${stickyMetricColumnClass} bg-card`} />
          <td colSpan={TABLE_REST_COL_SPAN} className="bg-card py-2" />
        </tr>
        <tr className="bg-muted/60 font-semibold">
          <td className={stickySectionColumnClass}>Расходы · {expenseCurrency}</td>
          <td colSpan={TABLE_REST_COL_SPAN} className="bg-muted/60 py-2" />
        </tr>
        {expenseLoading ? (
          <tr>
            <td className={stickyMetricColumnClass}>Загрузка…</td>
            <td colSpan={TABLE_REST_COL_SPAN} className="px-3 py-6 text-center text-muted-foreground">Загрузка расходов…</td>
          </tr>
        ) : (
          visibleExpenseGroups.map(group => {
            const groupValues = MONTH_LABELS.map((_, index) => financeGroupTotal(group, expensesByMonth.get(index + 1)?.values ?? {}))
            return (
              <Fragment key={group.code}>
                <tr className="bg-muted/30 font-semibold">
                  <td className={`${stickySectionColumnClass} !p-0`}>
                    <button
                      type="button"
                      onClick={() => toggleExpenseGroup(group.code)}
                      className="flex w-full items-center justify-between gap-2 py-2 pl-4 pr-3 text-left hover:bg-muted/60"
                      aria-expanded={expandedExpenseGroups.has(group.code)}
                    >
                      <span>{group.label}</span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 transition-transform ${expandedExpenseGroups.has(group.code) ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </td>
                  {groupValues.map((value, index) => (
                    <td key={index} className="bg-muted/30 px-3 py-2 text-right">
                      {value ? formatExpenseMoney(value, expenseCurrency) : '—'}
                    </td>
                  ))}
                  <td className="bg-muted/30 py-2 pl-4 pr-5 text-right">
                    {formatExpenseMoney(groupValues.reduce((sum, value) => sum + value, 0), expenseCurrency)}
                  </td>
                </tr>
                {expandedExpenseGroups.has(group.code) && group.fields.map(field => {
                  const values = MONTH_LABELS.map((_, index) => expensesByMonth.get(index + 1)?.values[field.code])
                  return (
                    <tr key={field.code} className="border-b border-border/50">
                      <td className={`${stickyMetricColumnClass} !pl-7`}>{field.label}</td>
                      {values.map((value, index) => (
                        <td key={index} className="px-3 py-2 text-right">
                          {value === undefined ? '—' : formatExpenseMoney(value, expenseCurrency)}
                        </td>
                      ))}
                      <td className="py-2 pl-4 pr-5 text-right">
                        {formatExpenseMoney(values.reduce((sum, value) => sum + (value ?? 0), 0), expenseCurrency)}
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            )
          })
        )}
        {expenseError && (
          <tr>
            <td className={`${stickyMetricColumnClass} text-red-600`}>Ошибка расходов</td>
            <td colSpan={TABLE_REST_COL_SPAN} className="px-3 py-3 text-sm text-red-600">{expenseError}</td>
          </tr>
        )}
        </>}
        </tbody>
      </table>
    </div>
  )
}
